// src/pages/Usage.tsx
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonChip,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  RefresherEventDetail,
  useIonToast,
} from '@ionic/react';
import { barChartOutline, cardOutline, pieChartOutline, sparkles, statsChartOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import TitleBar from '../components/TitleBar.tsx';
import { billing } from '../services/api.ts';

interface CreditBalance {
  user_id: string;
  balance: number;
  balance_display?: string;
  last_updated?: string;
  updated_on?: string;
}

interface CreditTransaction {
  id: string;
  user_id: string;
  transaction_type: 'purchase' | 'usage' | 'bonus' | 'refund';
  amount: number;
  balance_after: number;
  description: string;
  stripe_session_id?: string;
  ai_action_type?: string;
  price_display?: string;
  created_at?: string;
  created_on?: string;
}

interface UsageSummary {
  total_calls: number;
  total_credits_used: number;
  avg_credits_per_call: number;
  credits_by_feature: Record<string, number>;
  usage_over_time: Array<{ date: string; credits: number }>;
}

const Usage: React.FC = () => {
  const history = useHistory();
  const [present] = useIonToast();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'summary' | 'details'>('summary');
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);
  const [showDollarValue, setShowDollarValue] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const calculateSummary = (txs: CreditTransaction[]): UsageSummary => {
    // Filter for usage transactions only
    const usageTxs = txs.filter(tx => tx.transaction_type === 'usage');

    // Calculate total calls and credits
    const totalCalls = usageTxs.length;
    const totalCreditsUsed = Math.abs(usageTxs.reduce((sum, tx) => sum + tx.amount, 0));

    // Calculate credits by feature
    const creditsByFeature: Record<string, number> = {};
    usageTxs.forEach(tx => {
      const feature = tx.ai_action_type || 'other';
      creditsByFeature[feature] = (creditsByFeature[feature] || 0) + Math.abs(tx.amount);
    });

    // Calculate usage over time (group by date)
    const usageByDate: Record<string, number> = {};
    usageTxs.forEach(tx => {
      const dateStr = tx.created_on || tx.created_at || new Date().toISOString();
      const date = new Date(dateStr).toISOString().split('T')[0];
      usageByDate[date] = (usageByDate[date] || 0) + Math.abs(tx.amount);
    });

    const usageOverTime = Object.entries(usageByDate)
      .map(([date, credits]) => ({ date, credits }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      total_calls: totalCalls,
      total_credits_used: totalCreditsUsed,
      avg_credits_per_call: totalCalls > 0 ? totalCreditsUsed / totalCalls : 0,
      credits_by_feature: creditsByFeature,
      usage_over_time: usageOverTime,
    };
  };

  const loadData = async () => {
    try {
      const [balanceRes, transactionsRes] = await Promise.all([
        billing.getBalance(),
        billing.getTransactions(),
      ]);

      setBalance(balanceRes);
      const txs = transactionsRes.transactions || [];
      setTransactions(txs);

      // Calculate summary from transactions
      const summary = calculateSummary(txs);
      setUsageSummary(summary);
    } catch (error) {
      console.error('Failed to load usage data:', error);
      present({
        message: 'Failed to load credit usage data',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async (event: CustomEvent<RefresherEventDetail>) => {
    await loadData();
    event.detail.complete();
  }, []);

  const handleSegmentChange = (value: string) => {
    setActiveView(value as 'summary' | 'details');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const formatFullDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getFeatureColor = (feature: string): string => {
    const colors: Record<string, string> = {
      'resume_analysis': '#3880ff',
      'cover_letter': '#10dc60',
      'job_search': '#ffc409',
      'job_fit_analysis': '#f04141',
      'default': '#666'
    };
    return colors[feature] || colors.default;
  };

  const getFeatureLabel = (feature: string): string => {
    const labels: Record<string, string> = {
      'resume_analysis': 'Resume Analysis',
      'cover_letter': 'Cover Letter',
      'job_search': 'Job Search',
      'job_fit_analysis': 'Job Fit Analysis',
    };
    return labels[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'purchase':
      case 'bonus':
        return '#10dc60';
      case 'refund':
        return '#3880ff';
      case 'usage':
        return '#f04141';
      default:
        return '#666';
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <TitleBar title="Usage" />
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <IonSpinner />
          </div>
        ) : (
          <IonGrid className="ion-padding">
            <IonRow className="ion-justify-content-center">
              <IonCol size="12" sizeMd="10" sizeLg="8">
                {/* Balance Card */}
                <IonCard
                  style={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    marginBottom: '24px',
                    cursor: 'pointer',
                  }}
                >
                  <IonCardContent style={{ padding: '24px' }}>
                    <IonText style={{ color: 'white' }}>
                      <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                        Current Balance
                      </p>
                      <div
                        style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}
                        onClick={() => setShowDollarValue(!showDollarValue)}
                      >
                        <IonIcon
                          icon={sparkles}
                          style={{ fontSize: '32px', marginRight: '12px', color: '#FFD700' }}
                        />
                        {showDollarValue && balance?.balance_display ? (
                          <>
                            <span style={{ fontSize: '48px', fontWeight: 'bold' }}>
                              {balance.balance_display}
                            </span>
                            <span style={{ fontSize: '18px', marginLeft: '8px', opacity: 0.9 }}>
                              value
                            </span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: '48px', fontWeight: 'bold' }}>
                              {balance?.balance.toLocaleString() || 0}
                            </span>
                            <span style={{ fontSize: '18px', marginLeft: '8px', opacity: 0.9 }}>
                              credits
                            </span>
                          </>
                        )}
                      </div>
                      {balance?.balance_display && (
                        <p
                          style={{
                            fontSize: '13px',
                            opacity: 0.8,
                            marginBottom: '16px',
                            textAlign: 'center',
                            fontStyle: 'italic'
                          }}
                        >
                          Tap to see {showDollarValue ? 'credits' : 'dollar value'}
                        </p>
                      )}
                      <IonButton
                        expand="block"
                        fill="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          history.push('/app/settings/billing');
                        }}
                        style={{
                          '--border-color': 'white',
                          '--color': 'white',
                          '--border-radius': '12px',
                          height: '44px',
                          fontWeight: '600',
                        }}
                      >
                        Purchase More Credits
                      </IonButton>
                    </IonText>
                  </IonCardContent>
                </IonCard>

                {/* View Selector */}
                <IonSegment
                  value={activeView}
                  onIonChange={(e) => handleSegmentChange(e.detail.value as string)}
                  style={{ marginBottom: '24px' }}
                >
                  <IonSegmentButton value="summary">
                    <IonLabel>Summary</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="details">
                    <IonLabel>Transactions</IonLabel>
                  </IonSegmentButton>
                </IonSegment>

                {/* Summary View */}
                {activeView === 'summary' && usageSummary && (
                  <>
                    {/* Stats Cards */}
                    <IonGrid style={{ marginBottom: '16px' }}>
                      <IonRow>
                        <IonCol size="12" sizeMd="4">
                          <IonCard style={{ borderRadius: '12px', margin: '0 0 12px 0' }}>
                            <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                              <IonIcon icon={statsChartOutline} style={{ fontSize: '32px', color: '#3880ff', marginBottom: '8px' }} />
                              <IonText>
                                <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                                  {usageSummary.total_calls}
                                </h2>
                                <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>Total AI Calls</p>
                              </IonText>
                            </IonCardContent>
                          </IonCard>
                        </IonCol>
                        <IonCol size="12" sizeMd="4">
                          <IonCard style={{ borderRadius: '12px', margin: '0 0 12px 0' }}>
                            <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                              <IonIcon icon={sparkles} style={{ fontSize: '32px', color: '#10dc60', marginBottom: '8px' }} />
                              <IonText>
                                <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                                  {usageSummary.total_credits_used.toLocaleString()}
                                </h2>
                                <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>Credits Used</p>
                              </IonText>
                            </IonCardContent>
                          </IonCard>
                        </IonCol>
                        <IonCol size="12" sizeMd="4">
                          <IonCard style={{ borderRadius: '12px', margin: '0 0 12px 0' }}>
                            <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                              <IonIcon icon={pieChartOutline} style={{ fontSize: '32px', color: '#ffc409', marginBottom: '8px' }} />
                              <IonText>
                                <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                                  {usageSummary.avg_credits_per_call.toFixed(1)}
                                </h2>
                                <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>Avg Credits/Call</p>
                              </IonText>
                            </IonCardContent>
                          </IonCard>
                        </IonCol>
                      </IonRow>
                    </IonGrid>

                    {/* Credits by Feature */}
                    <IonCard style={{ borderRadius: '12px', marginBottom: '16px' }}>
                      <IonCardHeader style={{ paddingBottom: '12px' }}>
                        <IonText>
                          <h3 style={{ fontWeight: 'bold', fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center' }}>
                            <IonIcon icon={pieChartOutline} style={{ marginRight: '8px', fontSize: '20px' }} />
                            Credits by Feature
                          </h3>
                        </IonText>
                      </IonCardHeader>
                      <IonCardContent>
                        {Object.keys(usageSummary.credits_by_feature).length === 0 ? (
                          <IonText>
                            <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                              No usage data yet
                            </p>
                          </IonText>
                        ) : (
                          <div style={{ paddingTop: '8px' }}>
                            {Object.entries(usageSummary.credits_by_feature)
                              .sort(([, a], [, b]) => b - a)
                              .map(([feature, credits]) => {
                                const totalCredits = Object.values(usageSummary.credits_by_feature).reduce((a, b) => a + b, 0);
                                const percentage = (credits / totalCredits) * 100;
                                return (
                                  <div key={feature} style={{ marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{getFeatureLabel(feature)}</span>
                                      <span style={{ fontSize: '14px', color: '#666' }}>{credits} credits ({percentage.toFixed(0)}%)</span>
                                    </div>
                                    <div style={{ height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                                      <div
                                        style={{
                                          height: '100%',
                                          width: `${percentage}%`,
                                          backgroundColor: getFeatureColor(feature),
                                          transition: 'width 0.3s ease',
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </IonCardContent>
                    </IonCard>

                    {/* Usage Over Time */}
                    <IonCard style={{ borderRadius: '12px' }}>
                      <IonCardHeader style={{ paddingBottom: '12px' }}>
                        <IonText>
                          <h3 style={{ fontWeight: 'bold', fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center' }}>
                            <IonIcon icon={barChartOutline} style={{ marginRight: '8px', fontSize: '20px' }} />
                            Usage Over Time
                          </h3>
                        </IonText>
                      </IonCardHeader>
                      <IonCardContent>
                        {usageSummary.usage_over_time.length === 0 ? (
                          <IonText>
                            <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                              No usage data yet
                            </p>
                          </IonText>
                        ) : (
                          <div style={{ paddingTop: '8px' }}>
                            {usageSummary.usage_over_time.slice(-7).map((item, index) => {
                              const maxCredits = Math.max(...usageSummary.usage_over_time.map(d => d.credits));
                              const percentage = maxCredits > 0 ? (item.credits / maxCredits) * 100 : 0;
                              return (
                                <div key={index} style={{ marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '13px' }}>{formatDate(item.date)}</span>
                                    <span style={{ fontSize: '13px', fontWeight: '600' }}>{item.credits} credits</span>
                                  </div>
                                  <div style={{ height: '6px', backgroundColor: '#f0f0f0', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div
                                      style={{
                                        height: '100%',
                                        width: `${percentage}%`,
                                        backgroundColor: '#3880ff',
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </IonCardContent>
                    </IonCard>
                  </>
                )}

                {/* Transactions View */}
                {activeView === 'details' && (
                  <>
                    <IonText>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '16px', paddingLeft: '4px' }}>
                        Transaction History
                      </h3>
                    </IonText>

                    {transactions.length === 0 ? (
                      <IonCard style={{ borderRadius: '12px' }}>
                        <IonCardContent style={{ padding: '40px', textAlign: 'center' }}>
                          <IonIcon
                            icon={cardOutline}
                            style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }}
                          />
                          <IonText>
                            <p style={{ color: '#666' }}>No transactions yet</p>
                          </IonText>
                        </IonCardContent>
                      </IonCard>
                    ) : (
                      <IonList style={{ background: 'transparent' }}>
                        {transactions.map((tx) => {
                          const isExpanded = expandedTxId === tx.id;
                          const dateStr = tx.created_on || tx.created_at || '';
                          return (
                            <IonCard
                              key={tx.id}
                              style={{ borderRadius: '12px', marginBottom: '12px', cursor: 'pointer' }}
                              onClick={() => setExpandedTxId(isExpanded ? null : tx.id)}
                            >
                              <IonItem lines="none" style={{ '--padding-start': '16px', '--padding-end': '16px' }}>
                                <div
                                  slot="start"
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: `${getTransactionColor(tx.transaction_type)}20`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '12px',
                                  }}
                                >
                                  <IonIcon
                                    icon={sparkles}
                                    style={{ color: getTransactionColor(tx.transaction_type), fontSize: '20px' }}
                                  />
                                </div>
                                <IonLabel>
                                  <h2 style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>
                                    {tx.description || tx.transaction_type}
                                  </h2>
                                  <p style={{ fontSize: '13px', color: '#666' }}>
                                    {formatDate(dateStr)}
                                    {tx.ai_action_type && (
                                      <IonChip
                                        style={{
                                          marginLeft: '8px',
                                          height: '20px',
                                          fontSize: '11px',
                                          '--background': '#f0f0f0',
                                        }}
                                      >
                                        {getFeatureLabel(tx.ai_action_type)}
                                      </IonChip>
                                    )}
                                  </p>
                                </IonLabel>
                                <div slot="end" style={{ textAlign: 'right' }}>
                                  <IonText>
                                    {tx.price_display ? (
                                      <>
                                        <p
                                          style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: getTransactionColor(tx.transaction_type),
                                            marginBottom: '4px',
                                          }}
                                        >
                                          {tx.price_display}
                                        </p>
                                        <p style={{ fontSize: '12px', color: '#999' }}>
                                          +{tx.amount} credits
                                        </p>
                                      </>
                                    ) : (
                                      <>
                                        <p
                                          style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: getTransactionColor(tx.transaction_type),
                                            marginBottom: '4px',
                                          }}
                                        >
                                          {tx.amount > 0 ? '+' : ''}{tx.amount} credits
                                        </p>
                                        <p style={{ fontSize: '12px', color: '#999' }}>
                                          Balance: {tx.balance_after}
                                        </p>
                                      </>
                                    )}
                                  </IonText>
                                </div>
                              </IonItem>

                              {/* Expanded Details */}
                              {isExpanded && dateStr && (
                                <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid #f0f0f0' }}>
                                  <IonGrid style={{ padding: '12px 0 0 0' }}>
                                    <IonRow>
                                      <IonCol size="12">
                                        <div style={{ marginBottom: '8px' }}>
                                          <span style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>
                                            Full Timestamp
                                          </span>
                                          <span style={{ fontSize: '13px', color: '#333', fontFamily: 'monospace' }}>
                                            {formatFullDateTime(dateStr)}
                                          </span>
                                        </div>
                                      </IonCol>
                                      {tx.stripe_session_id && (
                                        <IonCol size="12">
                                          <div style={{ marginBottom: '8px' }}>
                                            <span style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>
                                              Session ID
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                              {tx.stripe_session_id}
                                            </span>
                                          </div>
                                        </IonCol>
                                      )}
                                      <IonCol size="6">
                                        <div>
                                          <span style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>
                                            Transaction ID
                                          </span>
                                          <span style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace' }}>
                                            {tx.id.substring(0, 8)}...
                                          </span>
                                        </div>
                                      </IonCol>
                                      <IonCol size="6">
                                        <div>
                                          <span style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>
                                            Balance After
                                          </span>
                                          <span style={{ fontSize: '13px', color: '#333', fontWeight: '600' }}>
                                            {tx.balance_after} credits
                                          </span>
                                        </div>
                                      </IonCol>
                                    </IonRow>
                                  </IonGrid>
                                </div>
                              )}
                            </IonCard>
                          );
                        })}
                      </IonList>
                    )}
                  </>
                )}
              </IonCol>
            </IonRow>
          </IonGrid>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Usage;
